import React, { useCallback, useEffect, useRef, useState } from "react";
import Layout from "../../components/layout";
import { FaArrowLeft, FaSearch, FaPlus, FaTimes, FaFileInvoice } from "react-icons/fa";
import { MdCheckBoxOutlineBlank, MdQrCodeScanner } from "react-icons/md";
import { ImPrinter } from "react-icons/im";

import styles from "./OpenOrderListPage.module.scss";
import { Link, useNavigate } from "react-router-dom";
import { route } from "../../static/route";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchOpenOrdersData,
  fetchPartiesData,
  getOrders,
  getParties,
  getSelectedOrder,
  getSelectedParty,
  getTotalOrders,
  updateSelectedOrder,
  updateSelectedParty,
} from "../../features/openOrder/openOrderSlice";
import ListCard from "../../components/orders/ListCard";
import { OptionsProps, OrderProps } from "../../types/order";
import Select from "react-select";
import { selectStyles } from "../../static/styles";
import { FaSquareCheck } from "react-icons/fa6";
import OrderPdf from "../../components/orderDetails/OrderPdf";
import { useReactToPrint } from "react-to-print";
import { GET } from "../../services/axios.service";
import { Html5Qrcode } from "html5-qrcode";

const printStyles = `
  @media print {
    @page {
      size: A6 portrait;
      margin: 0;
    }
  }
`;

const OpenOrderListPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  let debounceTimeout: NodeJS.Timeout;
  const printRef = useRef<HTMLDivElement>(null);
  const qrScannerRef = useRef<Html5Qrcode | null>(null);
  const orders = useAppSelector(getOrders);
  const totalOrders = useAppSelector(getTotalOrders);
  const parties = useAppSelector(getParties);
  const selectedParty = useAppSelector(getSelectedParty);
  const selectedOrder = useAppSelector(getSelectedOrder);

  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [firstLoading, setFirstLoading] = useState<boolean>(false);
  const [partiesOption, setPartiesOption] = useState<OptionsProps[]>([]);
  const [printOrders, setPrintOrders] = useState<OrderProps[]>([]);
  const [showFilter, setShowFilter] = useState(false);

  // Invoice state
  const [invoiceOrders, setInvoiceOrders] = useState<OrderProps[]>([]);
  const [showAddInvoice, setShowAddInvoice] = useState(false);
  const [addOrderId, setAddOrderId] = useState<string>("");
  const [addOrderError, setAddOrderError] = useState<string | null>(null);
  const [addOrderLoading, setAddOrderLoading] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [invoicePartyId, setInvoicePartyId] = useState<number | null>(null);

  const handleGoToBack = () => {
    navigate(route.dashboard);
  };

  const handleChangeParty = (id: number) => {
    dispatch(updateSelectedParty(id));
    setPage(1);
  };

  const handleChangeOrderId = (id: number | null) => {
    dispatch(updateSelectedOrder(id));
    setPage(1);
  };

  const handleChangePrintOrder = (order: OrderProps, action: boolean) => {
    setPrintOrders((prevOrders) => {
      if (action) {
        if (
          !prevOrders.some((existingOrder) => existingOrder.id === order.id)
        ) {
          return [...prevOrders, order];
        }
        return prevOrders;
      } else {
        return prevOrders.filter(
          (existingOrder) => existingOrder.id !== order.id
        );
      }
    });
  };

  const handleChangeInvoiceOrder = useCallback(
    (order: OrderProps, action: boolean) => {
      setAddOrderError(null);

      if (action) {
        // Validate party consistency
        if (invoiceOrders.length > 0) {
          const firstPartyId = invoiceOrders[0].party_id;
          if (order.party_id !== firstPartyId) {
            setAddOrderError("બધા ઓર્ડર એક જ પાર્ટીના હોવા જોઈએ.");
            return;
          }
        }

        setInvoiceOrders((prev) => {
          if (!prev.some((o) => o.id === order.id)) {
            return [...prev, order];
          }
          return prev;
        });

        if (invoiceOrders.length === 0) {
          setInvoicePartyId(order.party_id);
        }
      } else {
        setInvoiceOrders((prev) => {
          const updated = prev.filter((o) => o.id !== order.id);
          if (updated.length === 0) {
            setInvoicePartyId(null);
          }
          return updated;
        });
      }
    },
    [invoiceOrders]
  );

  const handleRemoveInvoiceOrder = useCallback((orderId: number) => {
    setInvoiceOrders((prev) => {
      const updated = prev.filter((o) => o.id !== orderId);
      if (updated.length === 0) {
        setInvoicePartyId(null);
      }
      return updated;
    });
  }, []);

  const handleAddOrderById = useCallback(
    async (orderId?: string) => {
      const id = orderId || addOrderId;
      if (!id || !id.trim()) {
        setAddOrderError("ઓર્ડર આઇડી દાખલ કરો.");
        return;
      }

      setAddOrderLoading(true);
      setAddOrderError(null);

      try {
        const response: any = await GET(`/v1/worker/orders/${id.trim()}`, {});

        if (response.data.status === "success" && response.data.data) {
          const order: OrderProps = response.data.data;

          if (order.status !== 0) {
            setAddOrderError("આ ઓર્ડર ચાલુ નથી.");
            setAddOrderLoading(false);
            return;
          }

          if (invoiceOrders.some((o) => o.id === order.id)) {
            setAddOrderError("આ ઓર્ડર પહેલેથી ઉમેરાયેલ છે.");
            setAddOrderLoading(false);
            return;
          }

          if (invoiceOrders.length > 0) {
            const firstPartyId = invoiceOrders[0].party_id;
            if (order.party_id !== firstPartyId) {
              setAddOrderError("બધા ઓર્ડર એક જ પાર્ટીના હોવા જોઈએ.");
              setAddOrderLoading(false);
              return;
            }
          }

          setInvoiceOrders((prev) => [...prev, order]);
          if (invoiceOrders.length === 0) {
            setInvoicePartyId(order.party_id);
          }
          setAddOrderId("");
        } else {
          setAddOrderError(
            response.data.message || "ઓર્ડર મળ્યો નથી."
          );
        }
      } catch (error: any) {
        setAddOrderError(
          error?.data?.message || "ઓર્ડર મળ્યો નથી."
        );
      }

      setAddOrderLoading(false);
    },
    [addOrderId, invoiceOrders]
  );

  const handleGoToDeliverOrders = useCallback(() => {
    if (invoiceOrders.length === 0) return;
    navigate(route.deliverOrders, {
      state: { orders: invoiceOrders },
    });
  }, [invoiceOrders, navigate]);

  const startQrScanner = useCallback(async () => {
    setShowQrScanner(true);

    // Wait for DOM element to be available
    setTimeout(async () => {
      try {
        const html5QrCode = new Html5Qrcode("qr-reader");
        qrScannerRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            // Extract order ID from QR code
            const orderId = decodedText.trim();
            handleAddOrderById(orderId);
            stopQrScanner();
          },
          () => {}
        );
      } catch (err) {
        setAddOrderError("QR સ્કેનર શરૂ થઈ શક્યું નથી.");
        setShowQrScanner(false);
      }
    }, 100);
  }, [handleAddOrderById]);

  const stopQrScanner = useCallback(() => {
    if (qrScannerRef.current) {
      qrScannerRef.current
        .stop()
        .then(() => {
          qrScannerRef.current = null;
        })
        .catch(() => {});
    }
    setShowQrScanner(false);
  }, []);

  const handlePrintOrderData = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "Open Order PDF",
  });

  useEffect(() => {
    if (page === 1) setFirstLoading(true);
    setIsLoading(true);

    dispatch(
      fetchOpenOrdersData({
        page: page,
        party_id: selectedParty ? selectedParty : undefined,
        order_id: selectedOrder ? selectedOrder : undefined,
      })
    ).then(() => {
      setIsLoading(false);
      setFirstLoading(false);
    });
  }, [page, selectedParty, selectedOrder]);

  useEffect(() => {
    dispatch(fetchPartiesData());
  }, []);

  useEffect(() => {
    if (parties) {
      const options: OptionsProps[] = [];
      parties?.map((party) => {
        options.push({ value: party.id, label: party.firm_name });
      });
      setPartiesOption(options);
    }
  }, [parties]);

  // Cleanup QR scanner on unmount
  useEffect(() => {
    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.stop().catch(() => {});
        qrScannerRef.current = null;
      }
    };
  }, []);

  return (
    <Layout>
      <div className={styles.OpenOrderListPage}>
        <div className="w-full px-10 h-[200px] text-center flex flex-col justify-center relative">
          <FaArrowLeft
            onClick={handleGoToBack}
            className="absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full"
          />

          <h1 className="text-5xl mt-6 font-bold text-white">ચાલુ કામ</h1>

          {printOrders.length > 0 && (
            <ImPrinter
              onClick={handlePrintOrderData}
              className="absolute top-5 right-5 text-3xl text-white"
            />
          )}
        </div>

        <div className="w-full relative mt-[70px] px-10 py-5">
          <div className="flex gap-2 mb-5">
            <button
              className="p-2 w-fit ml-auto rounded-md flex items-center bg-gradient-to-r from-primary-1 to-primary-2 text-white transition-colors duration-300"
              onClick={() => {
                setShowAddInvoice(false);
                setShowFilter(!showFilter);
              }}
            >
              <FaSearch className="mr-2" />{" "}
              {showFilter ? "ફિલ્ટર છુપાવો" : "ફિલ્ટર બતાવો"}
            </button>
            <button
              className="p-2 w-fit rounded-md flex items-center bg-gradient-to-r from-primary-1 to-primary-2 text-white transition-colors duration-300"
              onClick={() => {
                setShowFilter(false);
                setShowAddInvoice(!showAddInvoice);
              }}
            >
              <FaFileInvoice className="mr-2" />{" "}
              {showAddInvoice ? "ઈન્વૉઇસ છુપાવો" : "ઈન્વૉઇસ માં ઉમેરો"}
            </button>
          </div>

          {showFilter && (
            <>
              <div className="bg-gradient-to-r mb-5 from-primary-1 to-primary-2 h-16 w-full relative rounded">
                <Select
                  isClearable
                  value={partiesOption.find(
                    (val) => val.value === selectedParty
                  )}
                  className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                  styles={selectStyles}
                  placeholder={"પાર્ટી નું નામ દાખલ કરો"}
                  options={partiesOption}
                  onChange={(val: any) => handleChangeParty(val?.value)}
                />
              </div>
              <div className="bg-gradient-to-r mb-10 from-primary-1 to-primary-2 h-16 w-full relative rounded">
                <input
                  defaultValue={selectedOrder || ""}
                  className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                  placeholder="કામ નું આઇડી દાખલ કરો"
                  pattern="[0-9]*"
                  type="number"
                  onFocus={(e) =>
                    e.target.addEventListener(
                      "wheel",
                      function (e) {
                        e.preventDefault();
                      },
                      { passive: false }
                    )
                  }
                  onKeyUp={(e: any) => {
                    const value = e.target.value;

                    clearTimeout(debounceTimeout);
                    debounceTimeout = setTimeout(() => {
                      handleChangeOrderId(value || null);
                    }, 2000);
                  }}
                />
              </div>
            </>
          )}

          {/* Add to Invoice Section */}
          {showAddInvoice && (
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 mb-6">
              <h3 className="text-lg font-bold text-primary-1 mb-3 text-center">
                ઈન્વૉઇસ માં ઓર્ડર ઉમેરો
              </h3>

              {/* Party selector for invoice */}
              <div className="bg-gradient-to-r mb-3 from-primary-1 to-primary-2 h-14 w-full relative rounded">
                <Select
                  isClearable
                  value={partiesOption.find(
                    (val) => val.value === invoicePartyId
                  )}
                  className="text-primary-1 p-0 font-semibold text-lg placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                  styles={selectStyles}
                  placeholder={"પાર્ટી પસંદ કરો"}
                  options={partiesOption}
                  isDisabled={invoiceOrders.length > 0}
                  onChange={(val: any) =>
                    setInvoicePartyId(val?.value || null)
                  }
                />
              </div>

              {/* Order ID input + Add button */}
              <div className="flex gap-2 mb-3">
                <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-12 flex-1 relative rounded">
                  <input
                    value={addOrderId}
                    onChange={(e) => setAddOrderId(e.target.value)}
                    className="text-primary-1 px-3 py-1 font-semibold text-lg placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                    placeholder="ઓર્ડર આઇડી"
                    pattern="[0-9]*"
                    type="number"
                    onFocus={(e) =>
                      e.target.addEventListener(
                        "wheel",
                        function (e) {
                          e.preventDefault();
                        },
                        { passive: false }
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddOrderById();
                      }
                    }}
                  />
                </div>
                <button
                  onClick={() => handleAddOrderById()}
                  disabled={addOrderLoading}
                  className="px-4 h-12 rounded-md flex items-center bg-gradient-to-r from-primary-1 to-primary-2 text-white font-semibold disabled:opacity-50"
                >
                  {addOrderLoading ? (
                    "..."
                  ) : (
                    <>
                      <FaPlus className="mr-1" /> ઉમેરો
                    </>
                  )}
                </button>
                <button
                  onClick={() =>
                    showQrScanner ? stopQrScanner() : startQrScanner()
                  }
                  className={`px-4 h-12 hidden rounded-md flex items-center text-white font-semibold ${
                    showQrScanner
                      ? "bg-red-500"
                      : "bg-gradient-to-r from-primary-1 to-primary-2"
                  }`}
                >
                  <MdQrCodeScanner className="text-xl" />
                </button>
              </div>

              {/* QR Scanner */}
              {showQrScanner && (
                <div
                  id="qr-reader"
                  className="w-full mb-3 rounded-xl overflow-hidden"
                />
              )}

              {/* Error display */}
              {addOrderError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-xl mb-3 text-center text-sm">
                  {addOrderError}
                </div>
              )}

              {/* Selected orders pill list */}
              {invoiceOrders.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {invoiceOrders.map((order) => (
                    <span
                      key={order.id}
                      className="inline-flex items-center gap-1 bg-primary-1 bg-opacity-10 text-primary-1 font-semibold text-sm px-3 py-1.5 rounded-full"
                    >
                      #{order.id} - {order.item?.name || ""}
                      <FaTimes
                        onClick={() => handleRemoveInvoiceOrder(order.id)}
                        className="cursor-pointer text-red-500 ml-1"
                      />
                    </span>
                  ))}
                </div>
              )}

              {/* Generate Invoice button */}
              {invoiceOrders.length > 0 && (
                <button
                  onClick={handleGoToDeliverOrders}
                  className="w-full py-3 rounded-xl text-lg text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold shadow-lg"
                >
                  <FaFileInvoice className="inline mr-2" />
                  ઈન્વૉઇસ બનાવો ({invoiceOrders.length} ઓર્ડર)
                </button>
              )}
            </div>
          )}

          {firstLoading ? (
            <p className="mt-4 text-primary-2 font-bold text-2xl text-center">
              રાહ જુઓ...
            </p>
          ) : (
            <>
              {orders && orders.length > 0 ? (
                <div>
                  {orders.map((order, index) => (
                    <div className="relative w-full h-full" key={index}>
                      <Link
                        to={route.orderDetails + `/${order.id}`}
                      >
                        <ListCard order={order} />
                      </Link>
                      <div className="absolute top-3 right-3 flex gap-3">
                        {/* Print checkbox: printer icon above check/uncheck */}
                        {printOrders.some((o) => o.id === order.id) ? (
                          <div>
                            <ImPrinter className="text-xl text-primary-1" />
                            <FaSquareCheck
                              onClick={(e: any) => {
                                e.preventDefault();
                                handleChangePrintOrder(order, false);
                              }}
                              className="text-xl text-primary-1"
                            />
                          </div>
                        ) : (
                          <div>
                            <ImPrinter className="text-xl text-primary-1" />
                            <MdCheckBoxOutlineBlank
                              onClick={(e: any) => {
                                e.preventDefault();
                                handleChangePrintOrder(order, true);
                              }}
                              className="text-xl text-primary-1"
                            />
                          </div>
                        )}

                        {/* Invoice checkbox: invoice icon above check/uncheck */}
                        {invoiceOrders.some((o) => o.id === order.id) ? (
                          <div>
                            <FaFileInvoice className="text-xl text-primary-1" />
                            <FaSquareCheck
                              onClick={(e: any) => {
                                e.preventDefault();
                                handleChangeInvoiceOrder(order, false);
                              }}
                              className="text-xl text-primary-1"
                            />
                          </div>
                        ) : (
                          <div>
                            <FaFileInvoice className="text-xl text-primary-1" />
                            <MdCheckBoxOutlineBlank
                              onClick={(e: any) => {
                                e.preventDefault();
                                handleChangeInvoiceOrder(order, true);
                              }}
                              className="text-xl text-primary-1"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center">
                  <p className="mt-4 text-primary-2 font-bold text-2xl">
                    હાલ કોઈ કામ ની વિગતો મળી નથી.
                  </p>

                  <div className="w-full flex justify-center">
                    <Link to={route.newOrder}>
                      <button
                        type="button"
                        className="mx-auto mt-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                      >
                        કામ ઉમેરો
                      </button>
                    </Link>
                  </div>
                </div>
              )}

              {totalOrders > 0 && totalOrders > orders.length && (
                <div className="flex">
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={isLoading}
                    type="button"
                    className="mx-auto mt-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                  >
                    {isLoading ? "રાહ જુઓ..." : "વધુ જુઓ"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div ref={printRef} className="print:grid hidden">
        <style>{printStyles}</style>

        {printOrders.map((order, index) => (
          <OrderPdf order={order} key={index} />
        ))}
      </div>
    </Layout>
  );
};
export default OpenOrderListPage;
